// School Admin Service. Wraps all school-admin dashboard API calls using the shared apiClient.
import { apiGet, authFetch } from './apiClient';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WorkloadData {
  teachers: Array<{
    id: string;
    name: string;
    assignedAlerts: number;
    openAlerts: number;
    resolvedAlerts: number;
    resolutionRate: number;
    avgResolutionTime: number;
  }>;
  summary: { totalTeachers: number; totalOpen: number; totalResolved: number; avgResolutionRate: number };
}

export interface WebTrafficData {
  summary: { totalEvents: number; flaggedEvents: number; safeEvents: number; flaggedPercent: number };
  topDomains: Array<{ domain: string; visits: number; flagged: number }>;
  eventsByType: Array<{ type: string; count: number }>;
  hourlyHeatmap: Array<{ hour: number; count: number }>;
}

export interface MessageSafetyData {
  summary: { totalMessages: number; flaggedMessages: number; safeMessages: number; flaggedPercent: number };
  byPlatform: Array<{ platform: string; total: number; flagged: number }>;
}

export interface AIDetectionData {
  summary: {
    totalScans: number;
    alertsGenerated: number;
    suppressed: number;
    highConfidence: number;
    falsePositiveRate: number;
  };
  detectionTypes: Array<{ type: string; count: number; avgConfidence: number }>;
}

export interface LiveActivityData {
  activeStudents: number;
  highAlerts: number;
  criticalAlerts: number;
  eventsFlagged: number;
  recentEvents: Array<{
    id: string;
    studentName: string;
    eventType: string;
    content: string;
    severity: string;
    timestamp: string;
  }>;
}

export interface WellnessData {
  summary: {
    trackedStudents: number;
    trackedPercent: number;
    avgWellnessScore: number;
    avgScreenTime: number;
    atRiskCount: number;
  };
  atRiskStudents: Array<{ id: string; name: string; wellnessScore: number; screenTime: number; riskFactors: string[] }>;
  scoreDistribution: Array<{ range: string; count: number }>;
}

export interface SafetyScoreData {
  overall: number;
  trend: number;
  components: Array<{ name: string; score: number; weight: number }>;
  lastUpdated: string;
}

export interface AnalyticsOverview {
  totalStudents: number;
  totalTeachers: number;
  totalIncidents: number;
  activeAlerts: number;
  incidentTrend: number;
  alertTrend: number;
}

export interface AuditLogEntry {
  id: string;
  actorName: string;
  actorRole: string;
  action: string;
  targetTable: string;
  targetId: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface InviteData {
  summary: { pending: number; accepted: number; expired: number; acceptanceRate: number };
  invites: Array<{
    id: string;
    email: string;
    role: string;
    status: string;
    createdAt: string;
    expiresAt: string;
    acceptedAt?: string;
  }>;
}

export interface ClassroomInsight {
  id: string;
  name: string;
  subject: string | null;
  gradeLevel: number | null;
  studentCount: number;
  teachers: string[];
  totalIncidents: number;
  criticalIncidents: number;
  openIncidents: number;
  riskLevel: 'low' | 'medium' | 'high';
}

export interface AdminIncident {
  id: string;
  studentName: string;
  studentId: string;
  alertType: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'new' | 'acknowledged' | 'investigating' | 'resolved' | 'escalated' | 'dismissed';
  createdAt: string;
  updatedAt: string;
  assignedTo?: string;
  resolutionNotes?: string;
}

export interface AdminNotification {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  level: 'critical' | 'warning' | 'info';
  read: boolean;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const schoolAdminService = {
  async getWorkload(days = 30): Promise<WorkloadData> {
    return apiGet(`/dashboard/school-admin/workload?days=${days}`);
  },

  async getWebTraffic(days = 30): Promise<WebTrafficData> {
    return apiGet(`/dashboard/school-admin/web-traffic?days=${days}`);
  },

  async getMessageSafety(days = 30): Promise<MessageSafetyData> {
    return apiGet(`/dashboard/school-admin/message-safety?days=${days}`);
  },

  async getAIDetection(days = 30): Promise<AIDetectionData> {
    return apiGet(`/dashboard/school-admin/ai-detection?days=${days}`);
  },

  async getLiveActivity(): Promise<LiveActivityData> {
    return apiGet('/dashboard/school-admin/live-activity');
  },

  async getWellness(): Promise<WellnessData> {
    return apiGet('/dashboard/school-admin/wellness');
  },

  async getSafetyScore(): Promise<SafetyScoreData> {
    return apiGet('/dashboard/school-admin/safety-score');
  },

  async getAnalytics(days = 30): Promise<{ overview: AnalyticsOverview; [key: string]: unknown }> {
    return apiGet(`/dashboard/school-admin/analytics?days=${days}`);
  },

  async getClassrooms(days = 30): Promise<{ classrooms: ClassroomInsight[] }> {
    return apiGet(`/dashboard/school-admin/classrooms?days=${days}`);
  },

  async getIncidents(params?: { status?: string; severity?: string; search?: string }): Promise<{ incidents: AdminIncident[] }> {
    const query = new URLSearchParams();
    if (params?.status && params.status !== 'all') query.set('status', params.status);
    if (params?.severity && params.severity !== 'all') query.set('severity', params.severity);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return apiGet(`/dashboard/school-admin/incidents${qs ? `?${qs}` : ''}`);
  },

  async updateIncidentStatus(incidentId: string, status: string): Promise<void> {
    const res = await authFetch(`/dashboard/school-admin/incidents/${incidentId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error(`Failed to update incident: ${res.status}`);
  },

  async getAuditLog(page = 1, limit = 20): Promise<{ logs: AuditLogEntry[]; total: number; totalPages: number }> {
    return apiGet(`/dashboard/school-admin/audit-log?page=${page}&limit=${limit}`);
  },

  async getInvites(): Promise<InviteData> {
    return apiGet('/dashboard/school-admin/invites');
  },

  async getNotifications(): Promise<{ notifications: AdminNotification[] }> {
    return apiGet('/dashboard/school-admin/notifications');
  },

  async getProfile(): Promise<{ name: string; email: string; phone?: string; campusName?: string; district?: string; timezone?: string; schoolId?: string }> {
    return apiGet('/dashboard/school-admin/profile');
  },

  async saveProfile(data: Partial<{ name: string; email: string; phone: string; campusName: string; district: string; timezone: string }>): Promise<void> {
    const res = await authFetch('/dashboard/school-admin/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Failed to save profile: ${res.status}`);
  },
};
